/**
 * Cinematic transition presets for the message-to-media handoff.
 *
 * Used by:
 *   - PremiumAnnouncementReveal (wrapper around post-reveal children)
 *   - PhotoGallery (attached photos entrance)
 *
 * Resolution order (most specific wins):
 *   1. metadata.mediaTransition  (per-announcement override chosen in the wizard)
 *   2. THEME_DEFAULT_TRANSITION  (sensible default per animationTheme)
 *   3. 'blur_dissolve'           (global fallback — current behavior)
 *
 * Adding a new preset:
 *   - Add an entry to MEDIA_TRANSITION_PRESETS
 *   - Add a label to MEDIA_TRANSITION_LABELS
 *   - Optionally map themes to it in THEME_DEFAULT_TRANSITION
 */

import type { Transition } from 'framer-motion';

export type MediaTransitionId = 'fade' | 'blur_dissolve' | 'upward_motion';

export interface MediaTransitionPreset {
  id: MediaTransitionId;
  /** framer-motion initial state */
  initial: Record<string, number | string>;
  /** framer-motion animate state */
  animate: Record<string, number | string>;
  /** framer-motion transition */
  transition: Transition;
  /** Short, human-readable description for the wizard. */
  description: string;
}

const EASE = [0.22, 0.61, 0.36, 1] as const;

export const MEDIA_TRANSITION_PRESETS: Record<MediaTransitionId, MediaTransitionPreset> = {
  fade: {
    id: 'fade',
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    transition: { duration: 0.6, ease: EASE as any },
    description: 'A clean, gentle fade — minimal and editorial.',
  },
  blur_dissolve: {
    id: 'blur_dissolve',
    initial: { opacity: 0, filter: 'blur(12px)' },
    animate: { opacity: 1, filter: 'blur(0px)' },
    transition: { duration: 0.7, ease: EASE as any },
    description: 'A soft blur dissolve — dreamy and cinematic.',
  },
  upward_motion: {
    id: 'upward_motion',
    initial: { opacity: 0, y: 28, filter: 'blur(6px)' },
    animate: { opacity: 1, y: 0, filter: 'blur(0px)' },
    transition: { duration: 0.65, ease: EASE as any },
    description: 'Rises gently from below — warm and emotional.',
  },
};

export const MEDIA_TRANSITION_LABELS: Record<MediaTransitionId, string> = {
  fade: 'Fade',
  blur_dissolve: 'Blur Dissolve',
  upward_motion: 'Upward Motion',
};

/** Sensible default per animationTheme. Edit freely to retune. */
export const THEME_DEFAULT_TRANSITION: Record<string, MediaTransitionId> = {
  elegant_floral: 'upward_motion',
  golden_light: 'blur_dissolve',
  blooming_garden: 'upward_motion',
  handwritten_journal: 'fade',
  ornate_keepsake: 'blur_dissolve',
  memory_constellation: 'blur_dissolve',
};

const DEFAULT_PRESET: MediaTransitionId = 'blur_dissolve';

export function resolveMediaTransition(
  override?: string | null,
  animationTheme?: string | null,
): MediaTransitionPreset {
  if (override && override in MEDIA_TRANSITION_PRESETS) {
    return MEDIA_TRANSITION_PRESETS[override as MediaTransitionId];
  }
  if (animationTheme && animationTheme in THEME_DEFAULT_TRANSITION) {
    return MEDIA_TRANSITION_PRESETS[THEME_DEFAULT_TRANSITION[animationTheme]];
  }
  return MEDIA_TRANSITION_PRESETS[DEFAULT_PRESET];
}

export function getDefaultTransitionForTheme(animationTheme?: string | null): MediaTransitionId {
  if (animationTheme && animationTheme in THEME_DEFAULT_TRANSITION) {
    return THEME_DEFAULT_TRANSITION[animationTheme];
  }
  return DEFAULT_PRESET;
}
